import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * Downloads a file using the appropriate method for the current platform.
 * - iOS/Android native: Downloads via Capacitor Filesystem + Share sheet
 * - Web: Downloads via anchor tag click
 * 
 * @param url - The download URL
 * @param filename - The filename for the downloaded file
 */
export async function downloadFile(url: string, filename: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
        await downloadNative(url, filename);
    } else {
        downloadWeb(url, filename);
    }
}

/**
 * Native download: Filesystem.downloadFile → Share sheet
 */
async function downloadNative(url: string, filename: string): Promise<void> {
    const safeName = filename || 'download';

    try {
        const result = await Filesystem.downloadFile({
            url: url,
            path: safeName,
            directory: Directory.Cache,
        });

        if (result.path) {
            // Use 'files' (not 'url') to share local files. 'url' opens Safari.
            await Share.share({
                title: safeName,
                files: [result.path],
            });
        }
    } catch (error: any) {
        // User cancelled the share sheet — do nothing
        if (error?.message?.includes('canceled') || error?.message?.includes('cancelled')) {
            console.log('Share canceled by user');
            return;
        }
        console.warn('Native download failed, falling back to web download:', error);
        downloadWeb(url, filename);
    }
}

/**
 * Web download.
 *
 * 주의: HTML `<a download>` 속성은 **cross-origin URL(Supabase/Firebase)에서는 브라우저가 무시**한다.
 * 그래서 그냥 앵커를 클릭하면 다운로드 대신 현재 탭이 그 URL로 이동해 파일이 열려버린다
 * (txt·csv·json 처럼 브라우저가 렌더 가능한 포맷에서 특히 문제). zip 등 비렌더 포맷은 어차피
 * 다운로드되므로 그동안 드러나지 않았을 뿐이다.
 *
 * 강제 다운로드는 결국 **서버의 Content-Disposition: attachment 헤더**로만 cross-origin 에서 보장된다.
 * 따라서 우선순위:
 *   1) Supabase 공개 스토리지 URL → `?download` 파라미터로 서버가 attachment 를 설정하게 함
 *      (cross-origin OK, 메모리 0, 대용량 안전, 한글 파일명 정상). 같은 탭 앵커로 다운로드(페이지 이동 없음).
 *   2) URL 자체가 이미 attachment 를 강제(response-content-disposition 등, 예: Firebase 서명 URL)
 *      → 같은 탭 앵커로 그대로 다운로드.
 *   3) 그 외(강제 못 함) → 새 탭으로 열어 최소한 현재 앱 탭 파괴를 막는다(렌더 가능 파일은 새 탭에서 열림).
 */
function downloadWeb(url: string, filename: string): void {
    // 1) Supabase 공개 스토리지 → ?download 로 attachment 강제
    if (isSupabasePublicStorageUrl(url)) {
        triggerDownloadAnchor(appendDownloadParam(url, filename), filename, false);
        return;
    }
    // 2) 이미 서버가 attachment 를 강제하는 URL → 같은 탭에서 깔끔히 다운로드
    if (urlForcesAttachment(url)) {
        triggerDownloadAnchor(url, filename, false);
        return;
    }
    // 3) 강제 불가 cross-origin → 새 탭(앱 탭 보존). 렌더 가능 파일은 새 탭에서 열리고,
    //    실제 파일명 다운로드가 필요하면 사용자가 새 탭에서 저장.
    triggerDownloadAnchor(url, filename, true);
}

/** `/storage/v1/object/public/` 를 포함하면 Supabase 공개 스토리지 URL 로 간주 */
function isSupabasePublicStorageUrl(url: string): boolean {
    return url.includes('/storage/v1/object/public/');
}

/** URL 이 이미 Content-Disposition: attachment 를 유도하는지(서명 URL 의 response-content-disposition 등) */
function urlForcesAttachment(url: string): boolean {
    return /(response-)?content-disposition=[^&]*attachment|[?&]download(=|&|$)/i.test(url);
}

/** Supabase 공개 URL 에 `?download=<filename>` 파라미터를 붙여 attachment 강제 다운로드 URL 생성 */
function appendDownloadParam(url: string, filename: string): string {
    if (/[?&]download(=|&|$)/i.test(url)) return url; // 이미 있으면 그대로
    const sep = url.includes('?') ? '&' : '?';
    const name = (filename || '').trim();
    return name ? `${url}${sep}download=${encodeURIComponent(name)}` : `${url}${sep}download`;
}

/** 숨은 앵커를 만들어 클릭. newTab=true 면 새 탭으로 연다(현재 탭 이동 방지). */
function triggerDownloadAnchor(href: string, filename: string, newTab: boolean): void {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename || '';
    if (newTab) {
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
    }
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}
